/**
 * SSOT-зеркало ЕДИНОЙ серверной политики паролей.
 *
 * ADR: Backend/docs/adr/ADR-AUTH-unified-password-policy.md (решение владельца
 * 2026-07-16): одно правило для register / change-password / reset-password.
 *
 * ИСТОЧНИКИ ИСТИНЫ (Backend; при любом изменении читать ЦЕЛИКОМ, PIV п.10):
 *   - src/modules/auth/dto/register.dto.ts         (поле password)
 *   - src/modules/auth/dto/change-password.dto.ts  (поле newPassword)
 *   - src/modules/auth/dto/reset-password.dto.ts   (поле password)
 *
 * Регулярка скопирована ДОСЛОВНО: FE-валидация обязана быть НЕ слабее BE, иначе
 * пользователь получает «Validation failed» ПОСЛЕ сабмита (прецедент 2026-07-16).
 * Буквы — латинские (решение владельца: не-латиница не допускается, сообщение
 * говорит об этом явно). Гарантию на BE держит class-validator — доказано
 * исполнением в Backend/src/modules/auth/dto/password-policy.spec.ts.
 *
 * ЯЗЫК СООБЩЕНИЙ (AD-44). Модуль отдаёт КЛЮЧИ i18n и параметры к ним, а не
 * готовый текст. Раньше здесь лежали русские строки, и они уезжали в состояние
 * компонентов: немец на немецком экране получал русскую ошибку, а смена языка
 * уже показанное сообщение не переписывала. Ключ в состоянии переживает смену
 * языка, а сам модуль остаётся без зависимости от i18n и проверяется без него.
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export const PASSWORD_MIN_LENGTH_MESSAGE_KEY = "auth.validation.passwordMin";
export const PASSWORD_MAX_LENGTH_MESSAGE_KEY = "auth.validation.passwordMax";
export const PASSWORD_PATTERN_MESSAGE_KEY = "auth.validation.passwordPattern";

/**
 * Единое правило (дословно из BE DTO): строчная и заглавная латинские буквы +
 * (цифра ИЛИ спецсимвол).
 */
export const PASSWORD_PATTERN =
  /((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/;

/**
 * Нарушение политики: ключ сообщения и параметры подстановки.
 *
 * `count` — именно `count`, а не произвольное имя: у сообщений о длине заведены
 * плюральные формы i18next, а их выбирает только этот параметр. С любым другим
 * именем i18next вернул бы форму `_other` и по-русски получилось бы
 * «минимум 1 символов».
 */
export interface PasswordPolicyViolation {
  key: string;
  params?: { count: number };
}

/**
 * Валидация нового пароля (register / change / reset — правило единое).
 * @returns null — пароль пройдёт BE-валидацию; иначе нарушение для перевода.
 */
export function validatePassword(
  password: string,
): PasswordPolicyViolation | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      key: PASSWORD_MIN_LENGTH_MESSAGE_KEY,
      params: { count: PASSWORD_MIN_LENGTH },
    };
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      key: PASSWORD_MAX_LENGTH_MESSAGE_KEY,
      params: { count: PASSWORD_MAX_LENGTH },
    };
  }
  if (!PASSWORD_PATTERN.test(password)) {
    return { key: PASSWORD_PATTERN_MESSAGE_KEY };
  }
  return null;
}
